import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ShoppingBag, Sun, Moon, Languages, MapPin, Search, X } from "lucide-react";

export const Header = () => {
  const { t, lang, setLang, theme, setTheme, cartCount, setCartOpen, city, search, setSearch } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef(null);

  const openSearch = () => {
    setSearchOpen(true);
    if (!location.pathname.startsWith("/store/")) navigate("/store/orient");
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearch("");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <button
          data-testid="brand-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 shrink-0"
        >
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground shadow-lg shadow-primary/25">
            <ShoppingBag className="h-5 w-5" />
          </div>
          <div className="text-start leading-none">
            <div className="font-head font-extrabold text-lg tracking-tight">{t.brand}</div>
            {city && (
              <div className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                <MapPin className="h-3 w-3" /> {city}
              </div>
            )}
          </div>
        </button>

        <div className="flex items-center gap-1.5">
          <button
            data-testid="header-search-button"
            onClick={() => (searchOpen ? closeSearch() : openSearch())}
            className="h-10 w-10 rounded-xl border border-border bg-card hover:bg-accent transition-colors grid place-items-center"
            aria-label="search"
          >
            {searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </button>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "sv" ? "ar" : "sv")}
            className="h-10 px-3 rounded-xl border border-border bg-card hover:bg-accent transition-colors flex items-center gap-1.5 text-sm font-semibold"
          >
            <Languages className="h-4 w-4" />
            {lang === "sv" ? "عربي" : "SV"}
          </button>
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="h-10 w-10 rounded-xl border border-border bg-card hover:bg-accent transition-colors grid place-items-center"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            data-testid="cart-open-button"
            onClick={() => setCartOpen(true)}
            className="relative h-10 px-4 rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity flex items-center gap-2 font-semibold text-sm shadow-lg shadow-primary/25"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">{t.cart}</span>
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1.5 -end-1.5 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold grid place-items-center"
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-border bg-background/95 animate-ha-in">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 relative">
            <Search className="absolute top-1/2 -translate-y-1/2 start-7 sm:start-9 h-5 w-5 text-muted-foreground" />
            <input
              ref={inputRef}
              data-testid="header-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchStores}
              className="w-full h-12 rounded-2xl border border-border bg-card ps-12 pe-4 outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
